
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { ThemeToggle } from '../../components/ui/theme-toggle';
import { RippleButton } from '../../components/ui/ripple-button';
import { BackgroundGradientAnimation } from '../../components/ui/background-gradient-animation';
import { AnimatedTitle } from '../../components/ui/animated-title';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedPassword = localStorage.getItem('rememberedPassword');
    const wasRemembered = localStorage.getItem('rememberMe') === 'true';
    
    if (wasRemembered && savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      console.log('Iniciando login...', { email, isLogin });
      
      let result;
      try {
        // Adicionar timeout para evitar travamento
        const signInPromise = isLogin 
          ? signIn(email, password)
          : signUp(email, password);
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout: A requisição demorou muito')), 10000)
        );
        
        result = await Promise.race([signInPromise, timeoutPromise]);
        console.log('Resultado do signIn/signUp:', result);
      } catch (signError: any) {
        console.error('Erro ao executar signIn/signUp:', signError);
        const errorMessage = signError?.message || signError?.error?.message || 'Erro de conexão com o servidor';
        setError(errorMessage.includes('Timeout') 
          ? 'O servidor está demorando muito para responder. O projeto pode estar pausado.'
          : errorMessage);
        setIsSubmitting(false);
        return;
      }

      const { error } = result || {};

      if (error) {
        console.error('Erro no login:', error);
        setError(error.message);
        setIsSubmitting(false);
      } else if (isLogin) {
        console.log('Login bem-sucedido, verificando sessão...');
        
        // Aguardar um pouco para o Supabase processar o login
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
          localStorage.setItem('rememberedPassword', password);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberedEmail');
          localStorage.removeItem('rememberedPassword');
          localStorage.removeItem('rememberMe');
        }
        
        // Verificar a sessão diretamente após o login para garantir que está autenticado
        let session, sessionError;
        try {
          const sessionResult = await supabase.auth.getSession();
          session = sessionResult.data?.session;
          sessionError = sessionResult.error;
          console.log('Sessão verificada:', session ? 'Sessão encontrada' : 'Sem sessão', sessionError);
        } catch (sessionErr) {
          console.error('Erro ao verificar sessão:', sessionErr);
          setError('Erro ao verificar sessão. Tente novamente.');
          setIsSubmitting(false);
          return;
        }
        
        if (session) {
          console.log('Navegando para dashboard...');
          // Aguardar um pouco para garantir que o estado seja atualizado em todos os componentes
          await new Promise(resolve => setTimeout(resolve, 300));
          navigate('/dashboard', { replace: true });
          console.log('Navegação executada');
        } else {
          console.error('Sessão não encontrada após login', sessionError);
          setError(sessionError?.message || 'Erro ao verificar sessão. Tente novamente.');
          setIsSubmitting(false);
        }
      } else {
        setError('Conta criada! Verifique seu email para confirmar.');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Erro inesperado no login:', err);
      setError('Erro inesperado. Tente novamente.');
      setIsSubmitting(false);
    }
  };

  return (
    <BackgroundGradientAnimation
      gradientBackgroundStart="rgb(26, 15, 43)"
      gradientBackgroundEnd="rgb(13, 13, 13)"
      firstColor="106, 13, 173"
      secondColor="138, 43, 226"
      thirdColor="199, 128, 255"
      fourthColor="106, 13, 173"
      fifthColor="138, 43, 226"
      pointerColor="199, 128, 255"
      size="80%"
      blendingValue="hard-light"
      interactive={true}
      containerClassName="fixed inset-0"
      className="relative z-10 flex flex-col items-center justify-center p-4 min-h-screen"
    >
      <div className="w-full max-w-md relative z-20 flex flex-col items-center">
        {/* Animated Title - Acima do formulário */}
        <div className="flex justify-center mb-8 w-full">
          <AnimatedTitle className="text-2xl sm:text-3xl md:text-4xl text-white justify-center" />
        </div>
        {/* Formulário com glass effect melhorado */}
        <div className="bg-white/10 dark:bg-white/5 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 dark:border-white/10">
          {error && (
            <div className="mb-6 bg-red-500/20 backdrop-blur-sm border border-red-400/50 rounded-xl p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 dark:text-red-300 text-xl"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-200 dark:text-red-200 font-medium">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-white/70"></i>
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="block w-full pl-10 pr-4 py-3 bg-white/10 dark:bg-white/5 backdrop-blur-md border-2 border-white/20 dark:border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/30 transition-all duration-200 font-medium"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-white mb-2">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-white/70"></i>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    className="block w-full pl-10 pr-12 py-3 bg-white/10 dark:bg-white/5 backdrop-blur-md border-2 border-white/20 dark:border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/30 transition-all duration-200 font-medium"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/70 hover:text-white transition-colors duration-200"
                    disabled={isSubmitting}
                  >
                    <i className={showPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"}></i>
                  </button>
                </div>
              </div>
            </div>

            {isLogin && (
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-2 border-white/30 bg-white/10 focus:ring-white/50 focus:ring-2 cursor-pointer accent-white"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isSubmitting}
                />
                <label htmlFor="remember-me" className="ml-3 block text-sm text-white/90 cursor-pointer font-medium">
                  Lembrar dados de acesso
                </label>
              </div>
            )}

            <div>
              <RippleButton
                type="submit"
                disabled={isSubmitting}
                variant="default"
                size="lg"
                className="w-full flex items-center justify-center py-4 px-6 bg-gradient-to-r from-[#6a0dad] to-[#8a2be2] hover:from-[#8a2be2] hover:to-[#c780ff] text-white shadow-lg hover:shadow-2xl font-bold"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-3 animate-spin text-lg"></i>
                    {isLogin ? 'Entrando...' : 'Criando conta...'}
                  </>
                ) : (
                  <>
                    <i className={`${isLogin ? 'ri-login-box-line' : 'ri-user-add-line'} mr-3 text-lg`}></i>
                    {isLogin ? 'Entrar no Sistema' : 'Criar Conta'}
                  </>
                )}
              </RippleButton>
            </div>

            {!isLogin && (
              <div className="text-center">
                <button
                  type="button"
                  className="text-white/80 hover:text-white text-sm cursor-pointer font-semibold transition-colors duration-200 underline"
                  onClick={() => {
                    setIsLogin(true);
                    setError('');
                  }}
                  disabled={isSubmitting}
                >
                  Já tem conta? Fazer login
                </button>
              </div>
            )}
          </form>
        </div>
        
        {/* Rodapé */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-gray-400 dark:text-[#c780ff] text-xs font-medium">
            Sistema profissional para aceleração de projetos WordPress
          </p>
          <p className="text-gray-400 dark:text-[#c780ff] text-xs font-medium">
            created by:{' '}
            <a 
              href="https://startedi.net" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#6a0dad] dark:text-[#c780ff] hover:text-[#8a2be2] dark:hover:text-white underline transition-colors duration-200"
            >
              edisiqueira.io
            </a>
          </p>
        </div>
      </div>
    </BackgroundGradientAnimation>
  );
}
